import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DownloadGate from '../components/DownloadGate';
import GurujiChatWidget from '../components/GurujiChatWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FileText, Download, Filter, Loader2, Search, Calendar, Layers } from 'lucide-react';
import {
  upsertMetaByName,
  upsertCanonical,
  upsertJsonLd,
  removeJsonLd
} from '../lib/seoHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://vidya365.in';

const PYQ = () => {
  const [pyqs, setPyqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    exam_id: 'all',
    year: 'all',
    tier: 'all',
    language: 'all',
    search: ''
  });
  const [gateState, setGateState] = useState({ isOpen: false, pyq: null });

  useEffect(() => {
    fetchPyqs();
    // SEO
    const title = 'Free Previous Year Question Papers PDF — SSC, IBPS, RBI Grade B | Vidya365';
    const description = 'Download SSC CGL, IBPS PO, SBI PO, RBI Grade B previous year question papers (PYQ) PDFs for free. Last 5 years exam papers in English & Hindi.';
    document.title = title;
    upsertMetaByName('description', description);
    upsertMetaByName('keywords', 'SSC CGL PYQ, SSC CGL previous year paper, IBPS PO PYQ, SBI PO PYQ, RBI Grade B PYQ, free question paper PDF, SSC question paper download, Vidya365');
    upsertCanonical(`${SITE_URL}/pyq`);
    upsertJsonLd('pyq-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Previous Year Papers', item: `${SITE_URL}/pyq` }
      ]
    });
    return () => removeJsonLd('pyq-breadcrumb');
  }, []);

  const fetchPyqs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pyq`);
      const data = res.ok ? await res.json() : [];
      setPyqs(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Derived: unique filter values
  const exams = useMemo(() => {
    const map = {};
    pyqs.forEach(p => { map[p.exam_id] = p.exam_name; });
    return [{ id: 'all', name: 'All Exams' }, ...Object.entries(map).map(([id, name]) => ({ id, name }))];
  }, [pyqs]);

  const years = useMemo(
    () => ['all', ...Array.from(new Set(pyqs.map(p => p.year))).sort((a, b) => b - a)],
    [pyqs]
  );

  const filtered = useMemo(() => {
    return pyqs.filter(p => {
      if (filters.exam_id !== 'all' && p.exam_id !== filters.exam_id) return false;
      if (filters.year !== 'all' && String(p.year) !== String(filters.year)) return false;
      if (filters.tier !== 'all' && p.tier !== filters.tier) return false;
      if (filters.language !== 'all' && p.language !== filters.language) return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const haystack = `${p.exam_name} ${p.tier} ${p.shift || ''} ${p.exam_date || ''} ${p.year}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [pyqs, filters]);

  // Group filtered results by year
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(p => {
      if (!map[p.year]) map[p.year] = [];
      map[p.year].push(p);
    });
    return Object.entries(map).sort(([a], [b]) => Number(b) - Number(a));
  }, [filtered]);

  const openGate = (pyq) => setGateState({ isOpen: true, pyq });
  const closeGate = () => setGateState({ isOpen: false, pyq: null });

  const onGateSuccess = async () => {
    if (gateState.pyq) {
      // Fire-and-forget download counter increment
      fetch(`${API_URL}/api/pyq/${gateState.pyq.id}/track`, { method: 'POST' }).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 py-14 text-white" data-testid="pyq-hero">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Free PYQ Library</h1>
          <p className="text-lg md:text-xl text-red-100 max-w-2xl mx-auto">
            Last 5+ years previous year question papers — SSC, IBPS, SBI, RBI Grade B. Free download. English &amp; Hindi.
          </p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap text-sm">
            <Badge className="bg-white/20 hover:bg-white/30 text-white border-0 px-4 py-2">
              <FileText className="mr-2" size={16} /> {pyqs.length}+ Papers
            </Badge>
            <Badge className="bg-white/20 hover:bg-white/30 text-white border-0 px-4 py-2">
              <Layers className="mr-2" size={16} /> 2019–2024
            </Badge>
            <Badge className="bg-white/20 hover:bg-white/30 text-white border-0 px-4 py-2">
              <Download className="mr-2" size={16} /> 100% Free
            </Badge>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Sidebar Filters */}
            <aside className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center text-base">
                    <Filter className="mr-2" size={18} /> Filters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FilterSelect
                    testid="filter-exam"
                    label="Exam"
                    value={filters.exam_id}
                    onChange={(v) => setFilters({ ...filters, exam_id: v })}
                    options={exams.map(e => ({ value: e.id, label: e.name }))}
                  />
                  <FilterSelect
                    testid="filter-year"
                    label="Year"
                    value={String(filters.year)}
                    onChange={(v) => setFilters({ ...filters, year: v })}
                    options={years.map(y => ({ value: String(y), label: y === 'all' ? 'All Years' : y }))}
                  />
                  <FilterSelect
                    testid="filter-tier"
                    label="Tier / Stage"
                    value={filters.tier}
                    onChange={(v) => setFilters({ ...filters, tier: v })}
                    options={[
                      { value: 'all', label: 'All Tiers' },
                      { value: 'Tier 1', label: 'Tier 1' },
                      { value: 'Tier 2', label: 'Tier 2' },
                      { value: 'Prelims', label: 'Prelims' },
                      { value: 'Mains', label: 'Mains' }
                    ]}
                  />
                  <FilterSelect
                    testid="filter-language"
                    label="Language"
                    value={filters.language}
                    onChange={(v) => setFilters({ ...filters, language: v })}
                    options={[
                      { value: 'all', label: 'All Languages' },
                      { value: 'English', label: 'English' },
                      { value: 'Hindi', label: 'Hindi' }
                    ]}
                  />
                  <Button
                    data-testid="filter-clear"
                    variant="outline"
                    className="w-full"
                    onClick={() => setFilters({ exam_id: 'all', year: 'all', tier: 'all', language: 'all', search: '' })}
                  >
                    Clear All
                  </Button>
                </CardContent>
              </Card>
            </aside>

            {/* Main grid */}
            <div className="lg:col-span-3">
              {/* Search */}
              <div className="mb-6 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  data-testid="pyq-search"
                  type="text"
                  placeholder="Search by exam, date, shift…"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                />
              </div>

              {loading ? (
                <div className="text-center py-16">
                  <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
                </div>
              ) : grouped.length === 0 ? (
                <Card>
                  <CardContent className="py-16 text-center text-gray-600">
                    No PYQs found for the selected filters.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-8" data-testid="pyq-grouped">
                  {grouped.map(([year, list]) => (
                    <div key={year}>
                      <h2 className="text-2xl font-bold mb-4 flex items-center">
                        <Calendar className="mr-2 text-red-600" size={22} />
                        {year}
                        <span className="ml-2 text-sm text-gray-500">({list.length} papers)</span>
                      </h2>
                      <div className="grid md:grid-cols-2 gap-4">
                        {list.map(p => (
                          <PYQCard key={p.id} pyq={p} onDownload={() => openGate(p)} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <DownloadGate
        isOpen={gateState.isOpen}
        onClose={closeGate}
        downloadUrl={gateState.pyq?.pdf_url || ''}
        fileName={gateState.pyq ? `${gateState.pyq.exam_name} ${gateState.pyq.tier} ${gateState.pyq.exam_date || gateState.pyq.year} ${gateState.pyq.language}.pdf` : ''}
        resourceType="pyq"
        onSuccess={onGateSuccess}
      />

      <GurujiChatWidget />
      <Footer />
    </div>
  );
};

const FilterSelect = ({ testid, label, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-medium mb-2">{label}</label>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const PYQCard = ({ pyq, onDownload }) => (
  <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-red-500" data-testid={`pyq-card-${pyq.id}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex flex-wrap gap-2">
          <Badge className="bg-red-600 text-white">{pyq.exam_name}</Badge>
          <Badge variant="outline">{pyq.tier}</Badge>
          <Badge variant="outline" className={pyq.language === 'Hindi' ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700'}>
            {pyq.language}
          </Badge>
        </div>
      </div>
      <h3 className="font-semibold text-lg mb-1">
        {pyq.exam_date || `${pyq.year}`}{pyq.shift ? ` — ${pyq.shift}` : ''}
      </h3>
      <p className="text-sm text-gray-600 mb-4 flex flex-wrap items-center gap-3">
        <span className="flex items-center"><Calendar size={14} className="mr-1" /> {pyq.year}</span>
        {pyq.has_answer_key && <span className="text-green-600 font-medium">✓ With Answer Key</span>}
        {pyq.download_count > 0 && <span className="text-gray-500">{pyq.download_count} downloads</span>}
      </p>
      <Button
        data-testid={`pyq-download-${pyq.id}`}
        onClick={onDownload}
        className="w-full bg-red-600 hover:bg-red-700"
      >
        <Download className="mr-2" size={16} />
        Download PDF — Free
      </Button>
    </CardContent>
  </Card>
);

export default PYQ;
